import { AllGeoJSON, BBox, centroid } from "@turf/turf";
import bbox from "@turf/bbox";
import { Entity, GeoJSONGeometry } from "../types";
import { checkValidGeojsonAndSupportedType, wktToGeoJSON } from "./mapSearchUtils";

/**
 * Check if an entity has valid geometry that can be displayed on the map.
 * Returns false for:
 * - geometry: null
 * - geometry: { coordinates: [] }
 * - geometry: "LINESTRING EMPTY"
 */
export function hasValidGeometry(entity: Entity): boolean {
  // Check for direct coordinates first
  if (entity.coordinates) {
    const [lng, lat] = entity.coordinates;
    if (typeof lng === "number" && typeof lat === "number" && (lng !== 0 || lat !== 0)) {
      return true;
    }
  }
  // No geometry at all
  if (!entity.geometry) return false;
  // WKT string - check it's not empty
  if (typeof entity.geometry === "string") {
    const trimmed = entity.geometry.trim();
    return trimmed.length > 0 && !trimmed.toUpperCase().includes("EMPTY");
  }
  // GeoJSON object - check coordinates exist and are non-empty
  if (typeof entity.geometry === "object" && entity.geometry !== null) {
    const coords = (entity.geometry as any).coordinates;
    if (!coords) return false;
    if (Array.isArray(coords) && coords.length === 0) return false;
    return true;
  }
  return false;
}

/**
 * Check each coordinate to within valid range which -90 to above
 * @param coords - any valid formatted coordinates
 * @returns boolean
 * **/
export function hasValidCoordinates(coords: BBox | number[] | [number, number] | null | undefined) {
  if (!coords || !coords?.length) return false;
  return coords?.every((cord) => cord >= -90);
}

/**
 * Calculate centroid of given goejson
 * @param {AllGeoJSON} geojson
 * @returns { Point } point
 * */
export function getCoordinatesCentroid(geojson: AllGeoJSON) {
  if (!geojson) {
    throw new Error("Params is missing");
  }
  const result = centroid(geojson || []);

  const validCoords = hasValidCoordinates(result.geometry.coordinates);
  if (!validCoords) throw new Error("Invalid coordinates");
  return result?.geometry;
}

/**
 * Returns true if the given geometry (WKT string or GeoJSON object) intersects
 * the viewport bounding box defined by [minLng, minLat, maxLng, maxLat].
 */
export function isGeometryInViewport(
  geometry: any,
  minLng: number,
  minLat: number,
  maxLng: number,
  maxLat: number
): boolean {
  if (!geometry) return false;
  try {
    let geoJson: any;
    if (typeof geometry === "string") {
      geoJson = wktToGeoJSON(geometry);
      if (!geoJson) return false;
    } else if (checkValidGeojsonAndSupportedType(geometry)) {
      geoJson = geometry;
    } else {
      return false;
    }
    const [gMinLng, gMinLat, gMaxLng, gMaxLat] = bbox(geoJson);
    // Bboxes intersect when they are NOT disjoint on either axis
    return !(gMaxLng < minLng || gMinLng > maxLng || gMaxLat < minLat || gMinLat > maxLat);
  } catch {
    return false;
  }
}

/*
 * Strip Z dimensions from coordinates to ensure 2D compatibility with PostGIS
 * @param {GeoJSONGeometry} geoJSON
 * @returns {GeoJSONGeometry | any} GeoJSONGeometry
 */

export function stripZDimensions(geometry: GeoJSONGeometry | any): GeoJSONGeometry | any {
  if (!geometry || !geometry.type) return geometry;

  const stripCoordinates = (coords: any): any => {
    if (!Array.isArray(coords)) return coords;

    if (typeof coords[0] === "number") {
      // Single coordinate pair - keep only X,Y (first 2 elements)
      return coords.length > 2 ? coords.slice(0, 2) : coords;
    } else if (Array.isArray(coords[0])) {
      // Array of coordinates - recursively strip each
      return coords.map(stripCoordinates);
    }
    return coords;
  };

  const result = { ...geometry };

  // Handle coordinates property
  if (geometry.coordinates) {
    result.coordinates = stripCoordinates(geometry.coordinates);
  }

  // Handle GeometryCollection
  if (geometry.geometries && Array.isArray(geometry.geometries)) {
    result.geometries = geometry.geometries.map(stripZDimensions);
  }

  return result;
}

/**
 * Track-logger exports are not GeoJSON: they carry their route as a list of
 * `{ lon, lat, ele }` samples under a `trackData` key rather than as a `type` +
 * `coordinates` pair, so every GeoJSON code path — validation, the form's preview
 * map, the save payload — rejects them. This recognises that shape and returns the
 * equivalent MultiLineString (one line per track), leaving anything that is already
 * GeoJSON untouched.
 *
 * Elevation is preserved; Z is stripped further down, on save, by `stripZDimensions`.
 */
export function normaliseGeometryInput(parsed: any): any {
  if (!parsed || typeof parsed !== "object" || parsed.type) return parsed;

  // `trackData` sits at the top level in some exports and under `data` in others.
  const trackData = Array.isArray(parsed.trackData)
    ? parsed.trackData
    : Array.isArray(parsed.data?.trackData)
      ? parsed.data.trackData
      : null;
  if (!trackData) return parsed;

  const lines = trackData
    .map((track: any) => (Array.isArray(track) ? track.map(trackPointToPosition).filter(Boolean) : []))
    // A line needs two positions; a track logged as a single sample can't be drawn.
    .filter((line: any[]) => line.length >= 2);

  if (lines.length === 0) return parsed;
  return { type: "MultiLineString", coordinates: lines };
}

/** One `{ lon, lat, ele }` sample as a GeoJSON position, or null if it has no usable pair. */
function trackPointToPosition(point: any): number[] | null {
  if (!point || typeof point !== "object") return null;
  const lng = point.lon ?? point.lng ?? point.longitude;
  const lat = point.lat ?? point.latitude;
  if (typeof lng !== "number" || typeof lat !== "number") return null;
  const ele = point.ele ?? point.elevation ?? point.alt;
  return typeof ele === "number" ? [lng, lat, ele] : [lng, lat];
}
